import { useState, useEffect, useRef } from "react";
import Upload from "./components/Upload";
import ResultsTable, { type MatchResult } from "./components/ResultsTable";
import { isValidIsraeliId } from "./utils/israeliId";
import {
  getErrorMessage,
  isSupportedTranscriptFile,
  MAX_UPLOAD_BYTES,
  MESSAGES,
  readApiError,
} from "./utils/errors";

// API base URL - from environment variable
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";

/** Response from GET /api/track-config */
interface TrackConfig {
  tracks: string[];
  domainsByTrack: Record<string, string[]>;
  componentsByTrackDomain: Record<
    string,
    Record<string, { component: string; requiredHours: number }[]>
  >;
  hasMappingForTrackDomain: Record<string, Record<string, boolean>>;
  coursesByTrackDomainComponent: Record<
    string,
    Record<string, Record<string, { ourCourse: string; hours: number }[]>>
  >;
}

/** One match row in the by-component view (from API) */
interface MatchByComponentItem {
  ourCourse: string;
  ourHours: number;
  externalCourse: string;
  exemptedHours: number;
  grade: string;
}

interface MatchesByComponent {
  component: string;
  courses: MatchByComponentItem[];
}

interface AnalysisResult {
  studentName: string;
  studentId?: string;
  previousInstitution?: string;
  previousStudyPath?: string;
  matches: MatchResult[];
  unmatched: Array<{ courseName: string; grade: string }>;
  matchesByComponent?: MatchesByComponent[];
}

type StudentGender = "" | "גבר" | "אישה";

/** Step 1 selection summary persisted when moving to step 2 */
interface Step1Selection {
  track: string;
  domain: string;
  components: { component: string; requiredHours: number }[];
  totalRequiredHours: number;
}

export default function App() {
  const [step, setStep] = useState<1 | 2>(1);
  const [trackConfig, setTrackConfig] = useState<TrackConfig | null>(null);
  const [trackConfigError, setTrackConfigError] = useState<string | null>(null);
  const [trackConfigLoading, setTrackConfigLoading] = useState(true);
  const [selectedTrack, setSelectedTrack] = useState("");
  const [selectedDomain, setSelectedDomain] = useState("");
  const [expandedComponent, setExpandedComponent] = useState<string | null>(
    null,
  );
  /** Persisted when moving from step 1 to step 2 */
  const [step1Selection, setStep1Selection] = useState<Step1Selection | null>(
    null,
  );

  const [studentName, setStudentName] = useState("");
  const [studentId, setStudentId] = useState("");
  const [studentGender, setStudentGender] = useState<StudentGender>("");
  const [previousInstitution, setPreviousInstitution] = useState("");
  const [previousStudyPath, setPreviousStudyPath] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(
    null,
  );
  const [matches, setMatches] = useState<MatchResult[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [notes, setNotes] = useState("");

  // Derived counts for user feedback about transcript parsing
  const totalExtractedCourses =
    (analysisResult?.matches?.length ?? 0) +
    (analysisResult?.unmatched?.length ?? 0);
  const hasExtractedCourses = totalExtractedCourses > 0;

  // Iframe resizer - send height updates to parent window
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch track config on mount
  useEffect(() => {
    let cancelled = false;
    setTrackConfigLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/track-config`);
        if (!res.ok) {
          const msg = await readApiError(
            res,
            MESSAGES.TRACK_CONFIG_FAILED,
          );
          const err = new Error(msg) as Error & { status?: number };
          err.status = res.status;
          throw err;
        }
        const data = (await res.json()) as TrackConfig;
        if (!cancelled) setTrackConfig(data);
      } catch (e) {
        if (!cancelled) {
          setTrackConfigError(
            getErrorMessage(e, MESSAGES.TRACK_CONFIG_FAILED),
          );
        }
      } finally {
        if (!cancelled) setTrackConfigLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let resizeTimeoutId: NodeJS.Timeout;

    const updateHeight = () => {
      if (containerRef.current && window.parent !== window) {
        const height = containerRef.current.scrollHeight;
        window.parent.postMessage({ type: "resize", height }, "*");
      }
    };

    // Debounced update function to avoid too many calls
    const debouncedUpdate = () => {
      clearTimeout(resizeTimeoutId);
      resizeTimeoutId = setTimeout(updateHeight, 200);
    };

    // Initial update
    const initialTimeout = setTimeout(updateHeight, 100);

    const observer = new ResizeObserver(debouncedUpdate);

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    // Also update on window resize
    window.addEventListener("resize", debouncedUpdate);

    return () => {
      clearTimeout(initialTimeout);
      clearTimeout(resizeTimeoutId);
      observer.disconnect();
      window.removeEventListener("resize", debouncedUpdate);
    };
  }, [
    step,
    step1Selection !== null,
    analysisResult !== null,
    matches.length,
    error !== null,
    submitSuccess,
    trackConfig !== null,
    trackConfigLoading,
  ]);

  const handleFileSelect = async (selectedFile: File) => {
    const maxSizeMb = Math.round(MAX_UPLOAD_BYTES / 1024 / 1024);
    if (selectedFile.size > MAX_UPLOAD_BYTES) {
      setError(
        MESSAGES.FILE_TOO_LARGE(maxSizeMb) +
          ` (גודל הקובץ: ${(selectedFile.size / 1024 / 1024).toFixed(2)}MB)`,
      );
      return;
    }

    if (!isSupportedTranscriptFile(selectedFile.name, selectedFile.type)) {
      setError(MESSAGES.FILE_TYPE_UNSUPPORTED);
      return;
    }

    if (selectedFile.size === 0) {
      setError("הקובץ שהועלה ריק. אנא העלה קובץ תקין.");
      return;
    }

    setError(null);
    setSubmitSuccess(false);
    setPreviousInstitution("");
    setPreviousStudyPath("");
    setIsAnalyzing(true);

    // Save the uploaded file for later use in submit
    setUploadedFile(selectedFile);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      if (studentName) {
        formData.append("studentName", studentName);
      }
      if (step1Selection) {
        formData.append("track", step1Selection.track);
        formData.append("domain", step1Selection.domain);
      }

      console.log(
        "[FRONTEND] Sending request to:",
        `${API_BASE_URL}/api/analyze`,
      );
      console.log(
        "[FRONTEND] File:",
        selectedFile.name,
        "Size:",
        selectedFile.size,
      );

      const response = await fetch(`${API_BASE_URL}/api/analyze`, {
        method: "POST",
        body: formData,
      });

      console.log(
        "[FRONTEND] Response status:",
        response.status,
        response.statusText,
      );

      if (!response.ok) {
        const message = await readApiError(
          response,
          MESSAGES.ANALYZE_FAILED,
        );
        const err = new Error(message) as Error & { status?: number };
        err.status = response.status;
        throw err;
      }

      const result: AnalysisResult = await response.json();
      console.log(
        "[FRONTEND] Success! Received result with",
        result.matches?.length || 0,
        "matches",
      );
      setAnalysisResult(result);
      // Use functional update to avoid dependency issues
      setMatches(() => result.matches);

      // Update student name and ID if provided by AI
      if (result.studentName && !studentName) {
        setStudentName(result.studentName);
      }
      if (result.studentId && !studentId) {
        setStudentId(result.studentId.replace(/\D/g, "").slice(0, 9));
      }
      if (result.previousInstitution) {
        setPreviousInstitution(result.previousInstitution);
      }
      if (result.previousStudyPath) {
        setPreviousStudyPath(result.previousStudyPath);
      }
    } catch (err) {
      setError(getErrorMessage(err, MESSAGES.ANALYZE_FAILED));
      console.error("Analysis error:", err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSubmit = async () => {
    if (!studentName) {
      setError("אנא הזן שם סטודנט");
      return;
    }
    // Validate student ID is required
    if (!studentId || !studentId.trim()) {
      setError("אנא הזן תעודת זהות");
      return;
    }

    // Validate Israeli ID format (9 digits + check digit)
    if (!isValidIsraeliId(studentId)) {
      setError(
        "תעודת זהות לא תקינה. יש להזין 9 ספרות לפי נוסחת תעודת הזהות הישראלית.",
      );
      return;
    }

    if (studentGender !== "גבר" && studentGender !== "אישה") {
      setError("יש לבחור גבר או אישה");
      return;
    }

    // Validate that all matches have targets (only when there are matches)
    const invalidMatches = matches.filter((m) => !m.target || !m.target.trim());
    if (invalidMatches.length > 0) {
      setError("אנא מלא את כל שדות הפטור");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Send as FormData to include the file
      const formData = new FormData();
      formData.append("studentName", studentName);
      formData.append("studentId", studentId);
      formData.append("matches", JSON.stringify(matches));
      formData.append("notes", notes.trim());
      formData.append("gender", studentGender);
      formData.append("previousInstitution", previousInstitution);
      formData.append("previousStudyPath", previousStudyPath);
      if (step1Selection) {
        formData.append("track", step1Selection.track);
        formData.append("domain", step1Selection.domain);
      }
      formData.append(
        "matchesByComponent",
        JSON.stringify(analysisResult?.matchesByComponent ?? []),
      );
      formData.append(
        "componentRequirements",
        JSON.stringify(step1Selection?.components ?? []),
      );
      formData.append(
        "totalRequiredHours",
        String(step1Selection?.totalRequiredHours ?? 0),
      );

      // Add the uploaded file if it exists
      if (uploadedFile) {
        formData.append("file", uploadedFile);
        console.log(
          "[FRONTEND] Added file to FormData:",
          uploadedFile.name,
          "Size:",
          uploadedFile.size,
          "Type:",
          uploadedFile.type,
        );
      } else {
        console.warn(
          "[FRONTEND] WARNING: No uploadedFile found! Cannot attach file to email.",
        );
      }

      // Note: Don't set Content-Type header manually when sending FormData
      // The browser will automatically set it to multipart/form-data with boundary
      const response = await fetch(`${API_BASE_URL}/api/submit`, {
        method: "POST",
        body: formData,
        // Explicitly don't set Content-Type - let browser handle it
      });

      if (!response.ok) {
        const message = await readApiError(response, MESSAGES.SUBMIT_FAILED);
        const err = new Error(message) as Error & { status?: number };
        err.status = response.status;
        throw err;
      }

      setSubmitSuccess(true);
      // Reset form after successful submission
      setTimeout(() => {
        setAnalysisResult(null);
        setMatches([]);
        setStudentName("");
        setStudentId("");
        setStudentGender("");
        setPreviousInstitution("");
        setPreviousStudyPath("");
        setUploadedFile(null);
        setNotes("");
        setSubmitSuccess(false);
      }, 3000);
    } catch (err) {
      setError(getErrorMessage(err, MESSAGES.SUBMIT_FAILED));
      console.error("Submit error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const components =
    trackConfig && selectedTrack && selectedDomain
      ? (trackConfig.componentsByTrackDomain[selectedTrack]?.[selectedDomain] ??
        [])
      : [];
  const totalRequiredHours = components.reduce(
    (sum, c) => sum + c.requiredHours,
    0,
  );
  const coursesByComponent =
    trackConfig && selectedTrack && selectedDomain
      ? (trackConfig.coursesByTrackDomainComponent[selectedTrack]?.[
          selectedDomain
        ] ?? {})
      : {};
  const canGoNext = selectedTrack && selectedDomain && components.length > 0;

  const goToStep2 = () => {
    if (!canGoNext) return;
    setStep1Selection({
      track: selectedTrack,
      domain: selectedDomain,
      components,
      totalRequiredHours,
    });
    setStep(2);
  };

  return (
    <div ref={containerRef} className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {step === 1 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              בקשת הכרות
            </h1>
            {/* Intro - 3 placeholder lines */}
            <p className="text-gray-600 mb-4 whitespace-pre-line">
              {`תהליך ההכרות מאפשר לבדוק אילו קורסים מלימודיך הקודמים מזכים אותך בפטור.
בחר את מסלול הלימודים והתחום הרלוונטי אליך.
לאחר מכן תוכל להעלות את גיליון הציונים לחישוב אוטומטי של ההכרות.`}
            </p>

            {trackConfigError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-800 text-sm">{trackConfigError}</p>
              </div>
            )}

            {trackConfigLoading && !trackConfig && !trackConfigError && (
              <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
                <p className="text-gray-700 text-sm">טוען הגדרות...</p>
              </div>
            )}

            {trackConfig && (
              <>
                <div className="mb-4">
                  <label
                    htmlFor="track"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    מסלול לימודים
                  </label>
                  <select
                    id="track"
                    value={selectedTrack}
                    onChange={(e) => {
                      setSelectedTrack(e.target.value);
                      setSelectedDomain("");
                      setExpandedComponent(null);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">בחר מסלול</option>
                    {trackConfig.tracks.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedTrack && (
                  <div className="mb-6">
                    <label
                      htmlFor="domain"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      תחום
                    </label>
                    <select
                      id="domain"
                      value={selectedDomain}
                      onChange={(e) => {
                        setSelectedDomain(e.target.value);
                        setExpandedComponent(null);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">בחר תחום</option>
                      {(trackConfig.domainsByTrack[selectedTrack] ?? []).map(
                        (d) => (
                          <option key={d} value={d}>
                            {d}
                          </option>
                        ),
                      )}
                    </select>
                  </div>
                )}

                {selectedTrack && selectedDomain && components.length > 0 && (
                  <>
                    <p className="text-gray-700 mb-4">
                      סטודנט יקר, ב{selectedTrack}, במידה ולמדת {selectedDomain}
                      , יש לך סה&quot;כ {totalRequiredHours} ש&quot;ש לבצע במהלך
                      לימודיך, שהם מורכבים מהקורסים הבאים:
                    </p>
                    <ul className="list-none mb-6 space-y-2">
                      {components.map(({ component, requiredHours }) => {
                        const isExpanded = expandedComponent === component;
                        const courseList = coursesByComponent[component] ?? [];
                        return (
                          <li
                            key={component}
                            className="border border-gray-200 rounded-md overflow-hidden"
                          >
                            <button
                              type="button"
                              onClick={() =>
                                setExpandedComponent(
                                  isExpanded ? null : component,
                                )
                              }
                              className="w-full px-4 py-3 text-right flex items-center justify-between gap-2 bg-gray-50 hover:bg-gray-100"
                            >
                              <span>
                                {component} מורכב מ-{requiredHours} ש&quot;ש
                              </span>
                              <span className="text-gray-500" aria-hidden>
                                {isExpanded ? "▼" : "▶"}
                              </span>
                            </button>
                            {isExpanded && courseList.length > 0 && (
                              <ul className="px-4 py-2 bg-white border-t border-gray-200 list-none text-sm text-gray-700">
                                {courseList.map((c, i) => (
                                  <li key={i}>
                                    {c.ourCourse} ({c.hours} ש&quot;ש)
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    <p className="text-gray-600 mb-4">
                      בעמוד הבא תוכל להעלות את גיליון הציונים לחישוב אוטומטי של
                      ההכרות שיבוצעו עבורך.
                    </p>
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={goToStep2}
                        disabled={!canGoNext}
                        className="px-6 py-2 rounded-md font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        המשך לעמוד הבא
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              חישוב הכרות
            </h1>
            <p className="text-gray-600 mb-6 whitespace-pre-line">
              {`בעמוד זה תוכל להעלות את גיליון הציונים שלך. המערכת תזהה קורסים רלוונטיים ותציג אילו פטורים אתה זכאי לקבל בהתאם למסלול והתחום שבחרת.
ההכרות מחושבות בהתאם למיפוי הקורסים המוגדר במערכת.
לאחר ההעלאה יוצג סיכום לפי רכיבי לימודים וסה"כ ש"ש שנחסכו.`}
            </p>
            {step1Selection && (
              <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-gray-700 mb-2">
                  סטודנט יקר, ב{step1Selection.track}, במידה ולמדת{" "}
                  {step1Selection.domain}, יש לך סה&quot;כ{" "}
                  {step1Selection.totalRequiredHours} ש&quot;ש לבצע במהלך
                  לימודיך, שהם מורכבים מהקורסים הבאים:
                </p>
                <ul className="list-none space-y-1">
                  {step1Selection.components.map(
                    ({ component, requiredHours }) => {
                      const isExpanded = expandedComponent === component;
                      const courseList =
                        trackConfig?.coursesByTrackDomainComponent[
                          step1Selection.track
                        ]?.[step1Selection.domain]?.[component] ?? [];
                      return (
                        <li
                          key={component}
                          className="border border-gray-200 rounded-md overflow-hidden bg-white"
                        >
                          <button
                            type="button"
                            onClick={() =>
                              setExpandedComponent(
                                isExpanded ? null : component,
                              )
                            }
                            className="w-full px-4 py-2 text-right flex items-center justify-between gap-2 hover:bg-gray-50"
                          >
                            <span>
                              {component} מורכב מ-{requiredHours} ש&quot;ש
                            </span>
                            <span className="text-gray-500" aria-hidden>
                              {isExpanded ? "▼" : "▶"}
                            </span>
                          </button>
                          {isExpanded && courseList.length > 0 && (
                            <ul className="px-4 py-2 bg-gray-50 border-t border-gray-200 list-none text-sm text-gray-700">
                              {courseList.map((c, i) => (
                                <li key={i}>
                                  {c.ourCourse} ({c.hours} ש&quot;ש)
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      );
                    },
                  )}
                </ul>
              </div>
            )}

            {/* Student Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label
                  htmlFor="studentName"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  שם הסטודנט *
                </label>
                <input
                  id="studentName"
                  type="text"
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="הזן שם סטודנט"
                  disabled={isAnalyzing || isSubmitting}
                />
              </div>
              <div>
                <label
                  htmlFor="studentId"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  הזן תעודת זהות *
                </label>
                <input
                  id="studentId"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={9}
                  value={studentId}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "");
                    setStudentId(v);
                  }}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    studentId.trim() && !isValidIsraeliId(studentId)
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300"
                  }`}
                  placeholder="9 ספרות"
                  required
                  disabled={isAnalyzing || isSubmitting}
                />
                {studentId.trim() && !isValidIsraeliId(studentId) && (
                  <p className="mt-1 text-sm text-red-600">
                    תעודת זהות חייבת להכיל 9 ספרות ולעמוד בנוסחת הבדיקה
                  </p>
                )}
              </div>
              <div className="md:col-span-2">
                <label
                  htmlFor="studentGender"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  האם מדובר בגבר או באישה? (לצורך שליחת תוכנית לימודים בהתאמה) *
                </label>
                <select
                  id="studentGender"
                  value={studentGender}
                  onChange={(e) =>
                    setStudentGender(e.target.value as StudentGender)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isAnalyzing || isSubmitting}
                  required
                >
                  <option value="">בחר</option>
                  <option value="גבר">גבר</option>
                  <option value="אישה">אישה</option>
                </select>
              </div>
            </div>

            {/* Upload Component */}
            <div className="mb-6">
              <Upload
                onFileSelect={handleFileSelect}
                onError={setError}
                isAnalyzing={isAnalyzing}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            {/* Analysis status after uploading a transcript */}
            {analysisResult && !error && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md text-sm text-gray-800">
                {hasExtractedCourses ? (
                  <>
                    <p className="font-medium text-gray-900 mb-1">
                      גיליון הציונים נקרא בהצלחה.
                    </p>
                    <p>
                      זיהינו {totalExtractedCourses} קורסים בגיליון, מתוכם{" "}
                      {analysisResult.matches.length} קיבלו התאמה ראשונית
                      למערכת.
                    </p>
                    {(previousInstitution || previousStudyPath) && (
                      <div className="mt-2 pt-2 border-t border-blue-200">
                        {previousInstitution && (
                          <p>
                            <span className="font-medium">מוסד אקדמי קודם:</span>{" "}
                            {previousInstitution}
                          </p>
                        )}
                        {previousStudyPath && (
                          <p>
                            <span className="font-medium">מסלול לימודים קודמים:</span>{" "}
                            {previousStudyPath}
                          </p>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <p className="font-medium text-gray-900 mb-1">
                      בדקנו את גיליון הציונים אך לא הצלחנו לזהות בו קורסים.
                    </p>
                    <p>
                      ייתכן שיש בעיה באיכות הסריקה או בפורמט הקובץ. נסה להעלות
                      קובץ אחר או לסרוק מחדש את גיליון הציונים.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Success Message */}
            {submitSuccess && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                <p className="text-green-800 text-sm font-medium">
                  הבקשה נשלחה בהצלחה!
                </p>
              </div>
            )}

            {/* Results Table */}
            {analysisResult && (
              <div className="mb-6">
                <ResultsTable
                  matches={matches}
                  unmatched={analysisResult.unmatched}
                  onMatchesChange={setMatches}
                  matchesByComponent={analysisResult.matchesByComponent}
                  componentRequirements={step1Selection?.components}
                  totalRequiredHours={step1Selection?.totalRequiredHours ?? 0}
                />
              </div>
            )}

            {/* Notes (optional) - sent with submit and included in email + log */}
            <div className="mb-6">
              <label
                htmlFor="notes"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                הערות
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                placeholder="אם אתה סבור שמגיע לך פטור נוסף - אנא פרט כאן"
                disabled={isSubmitting}
                dir="rtl"
              />
            </div>

            {/* Submit Button */}
            {analysisResult && (
              <div className="flex flex-col items-end gap-2">
                <div className="flex justify-end">
                <button
                  onClick={handleSubmit}
                  disabled={
                    isSubmitting ||
                    !studentName ||
                    !studentId ||
                    !isValidIsraeliId(studentId) ||
                    (studentGender !== "גבר" && studentGender !== "אישה")
                  }
                  className={`
                  px-6 py-2 rounded-md font-medium text-white
                  ${
                    isSubmitting ||
                    !studentName ||
                    !studentId ||
                    !isValidIsraeliId(studentId) ||
                    (studentGender !== "גבר" && studentGender !== "אישה")
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }
                `}
                >
                  {isSubmitting ? "שולח..." : "אשר ושלח"}
                </button>
              </div>
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
